// Unleashed CSV importer DTOs (BUILD_SPEC.md §2.13, §5.9; plan.md G16). Owned by
// this goal — the ONLY core file G16 fills (the barrel, schemas/index.ts, already
// re-exports it — never edit that file).
//
// `IMPORT_JOB_KINDS` / `IMPORT_JOB_STATUSES` are defined LOCALLY rather than added
// to `@zebra/core/enums.ts` — enums.ts is G2's foundation file, out of scope for
// this goal's S0 registry rule (same precedent as schemas/sales.ts's
// `INVOICING_MODES` and schemas/stores.ts's `STORES_ISSUE_STATUSES`).
import { z } from 'zod';
import { moisturePctSchema, quantitySchema, ulidSchema } from './common.js';
export const IMPORT_JOB_KINDS = ['PRODUCTS', 'SUPPLIERS', 'CUSTOMERS', 'OPENING_BALANCES', 'FBA_SALES'];
export const IMPORT_JOB_STATUSES = ['DRAFT', 'VALIDATED', 'COMMITTED', 'FAILED'];
/** Unleashed CSV column -> our target field name. Keys are the CSV header text
 * verbatim; values are the field name the per-kind row schema expects (e.g.
 * `productCreateSchema`'s `sku`). A column absent from the map (or the map itself
 * omitted) falls back to identity — the CSV header IS already the field name. */
export const importColumnMappingSchema = z.record(z.string(), z.string());
/**
 * FBA_SALES job-level parameters (ruling by Stuart 2026-08-25: "build now,
 * generic CSV" — Howick's Amazon FBA sales, 441 orders/12mo, 0.4% of sales
 * value). Unlike every other kind, an FBA settlement CSV's rows carry no
 * customer/location/period of their own — those three are chosen ONCE per job
 * (a picker at job-creation time, not a CSV column) and stored in
 * `import_jobs.params` (see `schema/importer.ts`'s header). `periodLabel` becomes
 * the created dispatch's `notes` (dispatches has no dedicated `reference` column —
 * see `services/api/src/modules/importer/fba-sales.ts`) so Raise Invoices' period
 * grouping reads naturally.
 */
export const fbaSalesJobParamsSchema = z
    .object({
    customerId: ulidSchema,
    fromLocationId: ulidSchema,
    periodLabel: z.string().min(1, 'periodLabel is required').max(200),
})
    .strict();
export const importJobCreateSchema = z
    .object({
    kind: z.enum(IMPORT_JOB_KINDS),
    // v1 accepts the raw CSV text directly in the request body (goal text) — no real
    // object-store upload flow yet (see schema/importer.ts's `file_key` header note).
    csvContent: z.string().min(1, 'csvContent must not be empty'),
    mapping: importColumnMappingSchema.optional(),
    // Kind-specific job parameters — currently only FBA_SALES (see
    // fbaSalesJobParamsSchema below); every other kind must omit it. Loosely typed
    // here (validated by kind in the superRefine below) since each kind's params
    // shape differs.
    params: z.record(z.string(), z.unknown()).optional(),
})
    .superRefine((val, ctx) => {
    if (val.kind === 'FBA_SALES') {
        const parsed = fbaSalesJobParamsSchema.safeParse(val.params);
        if (!parsed.success) {
            for (const issue of parsed.error.issues) {
                ctx.addIssue({ ...issue, path: ['params', ...issue.path] });
            }
        }
    }
    else if (val.params !== undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'params is only accepted for FBA_SALES jobs', path: ['params'] });
    }
});
const listQueryBaseSchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
});
export const importJobListQuerySchema = listQueryBaseSchema.extend({
    kind: z.enum(IMPORT_JOB_KINDS).optional(),
    status: z.enum(IMPORT_JOB_STATUSES).optional(),
});
/**
 * OPENING_BALANCES row target shape (§5.9: "sku + location code + qty + optional mc
 * + unit_cost"). There is no existing target-table row schema to reuse here (an
 * opening balance is a ledger movement via `recordMovement`'s S1 seam, not a master
 * upsert) — this is the importer's own DTO. Validated once at `/validate` and
 * re-derived verbatim (same CSV + same schema) at `/commit`, so the two steps can
 * never disagree about what a row means.
 */
export const openingBalanceRowSchema = z.object({
    sku: z.string().min(1, 'sku is required'),
    locationCode: z.string().min(1, 'locationCode is required'),
    qty: quantitySchema.refine((v) => Number(v) > 0, 'qty must be greater than zero'),
    mcPct: moisturePctSchema.nullable().optional(),
    // Decimal cents PER BASE UNIT (kg) — matches ledger/record-movement.ts's
    // `MovementInput.unitCostCents` (a decimal string; the ledger rounds qty*unitCost
    // to the nearest cent itself). Optional: an opening balance with no known cost
    // basis commits at unit cost 0 (see the importer's commit handler).
    unitCostCents: z
        .string()
        .regex(/^\d+(\.\d+)?$/, 'unitCostCents must be a non-negative decimal')
        .nullable()
        .optional(),
});
/**
 * FBA_SALES row target shape — one line of an Amazon FBA settlement CSV: a
 * product code, a SIGNED quantity (negative = refund row — netted per-sku, see
 * `@zebra/core`'s `aggregateFbaSalesRows`; refund handling beyond simple netting
 * is out of scope) and a line revenue already converted to integer ZAR cents.
 *
 * `qty`/`revenueCents` are plain decimal STRINGS, not `quantitySchema`'s
 * unsigned form / `moneyCentsSchema`'s bigint — this row is boxed as `unknown`
 * into a job's `report` jsonb column (see `ImportRowResult.data`), and a raw
 * `bigint` cannot be `JSON.stringify`'d (matches `openingBalanceRowSchema`'s
 * `unitCostCents` precedent above, kept as a string for the same reason).
 * `date` is validated (if the optional DATE column is mapped) but never used to
 * split dispatches — commit aggregates the WHOLE job into one dispatch
 * regardless (goal ruling: "used only for the row report").
 */
export const fbaSalesRowSchema = z.object({
    sku: z.string().min(1, 'sku is required'),
    qty: z
        .string()
        .regex(/^-?\d+(\.\d{1,3})?$/, 'Invalid decimal quantity (max 3 decimal places)')
        .refine((v) => Number(v) !== 0, 'qty must not be zero'),
    revenueCents: z.string().regex(/^-?\d+$/, 'Invalid money cents value'),
    date: z.string().datetime().nullable().optional(),
});
/** One row's outcome inside a job's `report` jsonb, keyed by 1-based CSV data-row
 * number (row 1 = the first row after the header). `data` is the fully validated,
 * target-shaped value (only present when `ok`). `notes` is populated only for `ok`
 * rows where at least one numeric cell's raw text CHANGED under locale-number
 * normalization (see `@zebra/core`'s `parseLocaleNumber`, consumed by
 * services/api/src/modules/importer/rows.ts) — e.g. "qty: '1 234,56' -> '1234.56'"
 * — so a human can review the interpretation before committing. */
export const importRowResultSchema = z.object({
    row: z.number().int().positive(),
    ok: z.boolean(),
    errors: z.array(z.string()).optional(),
    data: z.unknown().optional(),
    notes: z.array(z.string()).optional(),
});
export const importReportSchema = z.object({
    totalRows: z.number().int().nonnegative(),
    okCount: z.number().int().nonnegative(),
    errorCount: z.number().int().nonnegative(),
    rows: z.array(importRowResultSchema),
    /** Populated only after a successful `/commit`. */
    committedCount: z.number().int().nonnegative().optional(),
});
//# sourceMappingURL=importer.js.map